/**
 * V2 Flexible Layout — Purchase Button Section Configuration
 *
 * FieldConfig + factory for FlexibleLayout Purchase Button sections.
 * Modeled after V2_PAYWALL_PURCHASE_BUTTON_CONFIG but with proxy-relative paths.
 *
 * Detection: Purchase buttons are Button sections with action.kind === 'purchase'.
 * No schema change needed — same SectionKind 'Button', same buttonContent field.
 *
 * Proxy paths (via FlexibleSectionContentAdapter):
 *   content.buttonContent        → section.buttonContent (TapTarget)
 *   content.buttonContent.content → section.buttonContent.content (TapTargetContent)
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  createTextStyleFields,
  createButtonContainerStyleFields,
  createV2ButtonAppearanceFields,
  createV2IconFields,
  V2_TEXT_ALIGN_OPTIONS,
} from './atomicFields';
import { createV2ButtonStyleGroups } from './v2ButtonStyleGroups';

/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * Purchase button field configuration for FlexibleLayout sections.
 *
 * Same proxy path mapping as regular Button config,
 * but with purchase-specific label, placeholder, and defaults.
 */
export const V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG: FieldConfig = {
  path: 'content.buttonContent',
  label: 'Purchase Button',
  placeholder: 'Subscribe Now',
  type: 'text',
  guideKey: 'screenedit-flexible-sections-purchase-button-text',
  l10nPath: 'content.buttonContent.content',
  defaultValue: 'Subscribe Now',
  disableBehavior: 'null',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.buttonContent.content')),
  buttonStyleFields: [
    ...createButtonContainerStyleFields('content.buttonContent').map(f => {
      let path = f.path.replace('.styles.', '.appearance.');
      if (path.endsWith('.appearance.borderWidth')) path = path.replace('.appearance.borderWidth', '.appearance.borders.width');
      else if (path.endsWith('.appearance.borderColor')) path = path.replace('.appearance.borderColor', '.appearance.borders.color');
      else if (path.endsWith('.appearance.borderRadius')) path = path.replace('.appearance.borderRadius', '.appearance.borders.radius.uniform');
      else if (path.endsWith('.appearance.height')) path = path.replace('.appearance.height', '.appearance.dimensions.height');
      else if (path.endsWith('.appearance.width')) path = path.replace('.appearance.width', '.appearance.dimensions.width');
      return { ...f, path };
    }),
    // V2-only appearance fields (gradient, shadow, opacity, animation)
    ...createV2ButtonAppearanceFields('content.buttonContent').map(f => ({
      ...f,
      path: f.path.replace('.styles.', '.appearance.'),
    })),
    // Icon fields
    ...createV2IconFields('content.buttonContent'),
    // Downsale: when enabled, payment cancel navigates to a downsale screen
    {
      path: 'content.buttonContent.action.onCancelAction',
      label: 'downsale on cancel',
      type: 'select',
      options: [{ value: 'enabled', label: 'Navigate to Downsale' }],
      defaultValue: 'enabled',
      isNullable: true,
      guideKey: 'screenedit-flexible-sections-purchase-button-downsale',
    },
  ],
  buttonStyleGroups: createV2ButtonStyleGroups('content.buttonContent', { includeOnCancelAction: true }),
  paddingConfig: {
    basePath: 'content.buttonContent.layout.insets',
    label: 'Box Padding',
  },
};

/**
 * Create default TapTarget structure for a purchase button.
 * Sets action.kind = 'purchase' which distinguishes it from regular buttons.
 */
export function createDefaultFlexiblePurchaseButton(
  locale: string,
  text: string = 'Subscribe Now',
): Record<string, unknown> {
  return {
    kind: 'label',
    content: {
      translations: { [locale]: text },
      kind: 'static',
      parameters: { labels: {}, links: {} },
      appearance: {
        fontSize: 18,
        fontFamily: 'SF Pro',
        fontWeight: 700,
        color: '#000000',
        lineHeight: 24,
        align: 'center',
      },
    },
    action: {
      kind: 'purchase',
      edges: [],
    },
    appearance: {
      backgroundColor: '#ffc201',
      borders: {
        radius: { uniform: 12 },
      },
    },
    layout: {
      insets: {
        top: 8,
        bottom: 8,
        start: 16,
        end: 16,
      },
    },
  };
}
